import React, { useState, useEffect, useCallback, useRef } from 'react';
import './Customer.css';
import Header from '../../components/Header';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import SuccessNotification from '../../components/Notifications/SuccessNotification';
import ErrorNotification from '../../components/Notifications/ErrorNotification';
import LoginNotification from "../../components/Notifications/LoginNotification";
import DeleteModal from '../../components/DeleteModal';
import { faEdit, faTrashAlt, faUser, faFileContract, faUserPlus, faEye, faSearch, faEllipsisH } from '@fortawesome/free-solid-svg-icons';
import Table from '../../components/Table';
import Section from '../../components/Section';
import SlideModal from '../../components/SlideModal';
import apiClient from "../../axios";
import { Tooltip } from "react-tooltip";
import { useNavigate } from 'react-router-dom';
import useEscapeKey from '../../hooks/useEscapeKey';

const Customer = ({ handleLogout }) => {
  const [data, setData] = useState([]);
  //const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [selectedItem, setSelectedItem] = useState(null);
  const itemsPerPage = 10;
  const [isSuccessVisible, setIsSuccessVisible] = useState(false);
  const [isErrorVisible, setIsErrorVisible] = useState(false);
  const [isInfoVisible, setIsInfoVisible] = useState(false);
  const navigate = useNavigate();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [deleteItem, setDeleteItem] = useState(false);
  const [selectedDeleteItem, setSelectedDeleteItem] = useState(null);
  const [openDropdown, setOpenDropdown] = useState(null);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      const isClickInsideDropdown = dropdownRef.current?.contains(event.target);
      const isDropdownItem = event.target.closest(".dm-class");

      if (!isClickInsideDropdown && !isDropdownItem) {
        setOpenDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEscapeKey(() => setIsModalOpen(false));

  const hasPermission = (permission) => {
    const permissions = JSON.parse(localStorage.getItem('permissions')) || [];
    return permissions.includes(permission);
  };

  const toggleModal = async (item) => {
    if (!isModalOpen) {
      try {
        const response = await apiClient.get(`/clients/${item.code}`);
        const customer = {
          general: item,
          alternative: response.data,
        };
        setSelectedItem(customer);
        setIsModalOpen(true);
      } catch (error) {
        setIsErrorVisible(true);
        if (error.response && error.response.status === 401) {
          setIsInfoVisible(true);
        }
      } finally {
        setIsSuccessVisible(true);
      }
    } else {
      setIsModalOpen(false);
    }
  };

  const fetchAllData = useCallback(async (page = 1) => {
    try {
      setLoading(true);
      const response = await apiClient.get(`/clients?page=${page}`);
      if (Array.isArray(response.data.results)) {
        setData(response.data.results);
        setTotalItems(response.data.total);
      } else {
        setData([response.data.results]);
        setTotalItems(1);
      }
    } catch (error) {
      setIsErrorVisible(true);
      if (error.response && error.response.status === 401) {
        setIsInfoVisible(true);
      }
    } finally {
      setIsSuccessVisible(true);
      setLoading(false);
    }
  }, [])

  const handleRefresh = () => {
    fetchAllData(currentPage);
  };

  const handleSearch = useCallback(async (page = 1) => {
    try {
      // Construir la URL con los query params
      const params = new URLSearchParams();
      if (searchTerm) params.append('search', searchTerm);

      const response = await apiClient.get(`/clients?${params.toString()}&page=${page}`);

      if (Array.isArray(response.data.results)) {
        setData(response.data.results); // Si es un array, lo dejamos tal cual
      } else {
        setData([response.data.results]); // Si es un objeto, lo encapsulamos en un array
      }
      setTotalItems(response.data.total);
    } catch (error) {
      if (error.response && error.response.status === 401) {
        setIsInfoVisible(true);
      }
    }
  }, [searchTerm]);

  const handleFresh = async () => {
    //handleSearch(1)
    setCurrentPage(1);
    setSearchTerm(searchInput.trim());
  };

  useEffect(() => {
    if (searchTerm) {
      handleSearch(currentPage);
    } else {
      fetchAllData(currentPage);
    }

  }, [fetchAllData, handleSearch, searchTerm, currentPage]);

  const handleDeleteModal = (item) => {
    setSelectedDeleteItem(item);
    setDeleteItem(true);
  }
  const handleCloseDeleteModal = () => {
    setDeleteItem(false);
    setSelectedDeleteItem(null);
    handleRefresh()
  };

  const columns = [
    { title: "Código", key: "code" },
    { title: "Razon Social", key: "socialReason" },
    { title: "Cédula", key: "cif" },
    { title: "Creado", key: "created" },
    { title: "Acciones", key: "acciones" },
  ];

  const renderRow = (item, index) => (
    <>
      <td>{item.code}</td>
      <td>{item.socialReason}</td>
      <td>{item.cif}</td>
      <td>{new Date(item.created).toLocaleString()}</td>
      <td>
        <div className="button-container">
          <div className="desktop-buttons">
            {hasPermission('CUSTOMER_VIEW') && (
              <button
                className="icon-button company-button"
                onClick={() => toggleModal(item)}
                data-tooltip-id="contract-tooltip"
                data-tooltip-content="Detalle"
              >
                <FontAwesomeIcon icon={faEye} />
              </button>
            )}
            <Tooltip id="edit-tooltip" className="custom-tooltip" />
            {hasPermission('CUSTOMER_UPDATE') && (
              <button
                data-tooltip-id="edit-tooltip"
                className="icon-button edit-button"
                data-tooltip-content="Editar"
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(`/ClientForm/${item.code}`);
                }}
              >
                <FontAwesomeIcon icon={faEdit} />
              </button>
            )}
            <Tooltip id="delete-tooltip" className="custom-tooltip" />
            {hasPermission('CUSTOMER_DELETE') && (
              <button
                data-tooltip-id="delete-tooltip"
                data-tooltip-content="Eliminar"
                className="icon-button delete-button"
                onClick={() => handleDeleteModal(item)}
              //onClick={() => handleDelete(item.code)}
              >
                <FontAwesomeIcon icon={faTrashAlt} />
              </button>
            )}
            <Tooltip id="contract-tooltip" className="custom-tooltip" />
            {hasPermission('CONTRACT_LIST') && (
              <button
                className="icon-button company-button"
                onClick={(e) => {
                  e.stopPropagation();
                  navigate('/Contract', { state: { customer: item } });
                }}
                data-tooltip-id="contract-tooltip"
                data-tooltip-content="Contratos"
              >
                <FontAwesomeIcon icon={faFileContract} />
              </button>
            )}
          </div>

          <div className="mobile-dropdown">
            <div className="dropdown-container-contract">
              <button
                className="icon-button dropdown-button-contract"
                onClick={(e) => {
                  e.stopPropagation();
                  setOpenDropdown(openDropdown === item.code ? null : item.code);
                }}>
                <FontAwesomeIcon icon={faEllipsisH} />
              </button>
              {openDropdown === item.code && (
                <div className="dropdown-menu-contract dm-class">
                  {hasPermission('CUSTOMER_UPDATE') && (
                    <button
                      className="dropdown-item-contract"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/ClientForm/${item.code}`);
                        setOpenDropdown(false);
                      }}>Editar
                    </button>
                  )}
                  {hasPermission('CUSTOMER_DELETE') && (
                    <button
                      className="dropdown-item-contract"
                      onClick={() => {
                        handleDeleteModal(item);
                        setOpenDropdown(false);
                      }}>
                      Eliminar
                    </button>
                  )}
                  {hasPermission('CONTRACT_LIST') && (
                    <button
                      className="dropdown-item-contract"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate('/Contract', { state: { customer: item } });
                        setOpenDropdown(false);
                      }}>Contratos
                    </button>
                  )}
                  {hasPermission('CUSTOMER_VIEW') && (
                    <button
                      className="dropdown-item-contract"
                      onClick={() => {
                        toggleModal(item);
                        setOpenDropdown(false);
                      }}>Detalle
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>

        </div>
      </td>
    </>
  );

  return (
    <div className="home-container">
      <Header onLogout={handleLogout} title='Clientes' />
      <div className="home-content">
        <Section>
          <div className="filter-form">
            <div className="basic-info-form-group">
              <input
                type="text"
                id="customer"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="Buscar"
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    handleFresh();
                  }
                }}
              />
            </div>
            <button className="basic-custom-button" onClick={handleFresh}>
              <FontAwesomeIcon icon={faSearch} className="basic-shortcut-icon" />
              Buscar
            </button>

          </div>
          <div className="button-add">
            {hasPermission('CUSTOMER_CREATE') && (
              <button
                className="basic-custom-button"
                onClick={(e) => {
                  e.stopPropagation();
                  navigate('/ClientForm');
                }}
              >
                <FontAwesomeIcon className="basic-shortcut-icon" icon={faUserPlus} />
                Crear Nuevo Cliente
              </button>
            )}
          </div>
        </Section>

        <Table
          title="Lista de Clientes"
          rows={data}
          columns={columns}
          icon={faUser}
          renderRow={renderRow}
          currentPage={currentPage}
          totalItems={totalItems}
          itemsPerPage={itemsPerPage}
          onPageChange={setCurrentPage}
          onRefresh={handleRefresh}
          loading={loading}
          flagPagination={true}
        />
        <SuccessNotification
          message={"Se ha cargado correctamente"}
          isVisible={isSuccessVisible}
          onClose={() => setIsSuccessVisible(false)}
        />
        <ErrorNotification
          message="Ups! Ocurrió un Problema"
          isVisible={isErrorVisible}
          onClose={() => setIsErrorVisible(false)}
        />
        <LoginNotification
          message="Vuelve a iniciar sesión"
          isVisible={isInfoVisible}
          onClose={() => setIsInfoVisible(false)}
          onLogout={handleLogout}
        />
        {selectedDeleteItem && (
          <DeleteModal
            isVisible={deleteItem}
            onClose={handleCloseDeleteModal}
            identificator={selectedDeleteItem.code}
            url={`/clients`}
          />
        )}
        <SlideModal isOpen={isModalOpen} onClose={toggleModal} title="Detalles del Cliente">
          <div className="section-container-form">
            <h4 className="section-title-form">CÓDIGO</h4>
            <hr className="section-divider-form" />
            <div className="form-group-form">
              <label>{selectedItem?.general.code}</label>
            </div>

            <h4 className="section-title-form">RAZON SOCIAL</h4>
            <hr className="section-divider-form" />
            <div className="form-group-form">
              <label>{selectedItem?.general.socialReason}</label>
            </div>

            <h4 className="section-title-form">CÉDULA</h4>
            <hr className="section-divider-form" />
            <div className="form-group-form">
              <label>{selectedItem?.general.cif}</label>
            </div>

            <h4 className="section-title-form">NOMBRE COMERCIAL</h4>
            <hr className="section-divider-form" />
            <div className="form-group-form">
              <label>{selectedItem?.general.comercialName}</label>
            </div>
            <h4 className="section-title-form">DIRECCIÓN</h4>
            <hr className="section-divider-form" />
            <div className="form-group-form">
              <label>{selectedItem?.alternative.direc1 || selectedItem?.alternative.direc2}</label>
            </div>
            <h4 className="section-title-form">DISTRIBUIDOR</h4>
            <hr className="section-divider-form" />
            <div className="form-group-form">
              <label>{selectedItem?.alternative.distribuidor}</label>
            </div>
            <h4 className="section-title-form">TELÉFONO</h4>
            <hr className="section-divider-form" />
            <div className="form-group-form">
              <label>{selectedItem?.alternative.tlf1}</label>
            </div>
            <h4 className="section-title-form">GRUPO</h4>
            <hr className="section-divider-form" />
            <div className="form-group-form">
              <label>{selectedItem?.alternative.customerTypeDescription}</label>
            </div>
            <h4 className="section-title-form">CLIENTE FINAL</h4>
            <hr className="section-divider-form" />
            <div className="form-group-form">
              <label>{selectedItem?.general.isStaff === 1 ? "Sí" : "No"}</label>
            </div>
          </div>
        </SlideModal>
      </div>
    </div>
  );
};

export default Customer;
